import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  List,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemButton,
  alpha,
} from '@mui/material';
import {
  Home as HomeIcon,
  Add as AddIcon,
  List as ListIcon,
  CheckCircle as CheckCircleIcon,
  BarChart as BarChartIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  Lightbulb as LightbulbIcon,
  Link as LinkIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getCurrentSchoolYear } from '../lib/tables';

const drawerWidth = 260;

export default function DesktopLayout({ children, session }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const getNavItems = () => {
    if (!profile) return [];
    
    const baseItems = [
      { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' },
    ];

    if (profile.rol === 'elev') {
        return [
        ...baseItems,
        { label: 'Cerere Nouă', icon: <AddIcon />, path: '/new-request' },
        { label: 'Cererile Mele', icon: <ListIcon />, path: '/my-requests' },
        { label: 'Sugestii', icon: <LightbulbIcon />, path: '/suggestions' },
        { label: 'Integrare cu SRBHub', icon: <LinkIcon />, path: '/profil/integrari' },
        // { label: 'Beneficii', icon: <CardGiftcardIcon />, path: '/benefits' }, // Temporar dezactivat
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
        ];
    }

    if (profile.rol === 'profesor') {
      return [
        ...baseItems,
        { label: 'Elevii Mei', icon: <GroupsIcon />, path: '/teacher-students' },
        { label: 'Validare Cereri', icon: <CheckCircleIcon />, path: '/review-requests' },
        { label: 'Statistici', icon: <BarChartIcon />, path: '/intelligence' },
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
      ];
    }

    if (profile.rol === 'coordonator') {
      return [
        ...baseItems,
        { label: 'Validare Cereri', icon: <CheckCircleIcon />, path: '/review-requests' },
        { label: 'Sistem Inteligent', icon: <BarChartIcon />, path: '/intelligence' },
        { label: 'Toate cererile', icon: <ListIcon />, path: '/coordonator/cereri' },
        { label: 'Mutare Clasă', icon: <SwapHorizIcon />, path: '/class-management' },
        { label: 'Integrări SRBHub', icon: <LinkIcon />, path: '/coordonator/integrari' },
        { label: 'Audit Integrări', icon: <FactCheckIcon />, path: '/coordonator/audit-integrari' },
        { label: 'Jurnal Audit', icon: <HistoryIcon />, path: '/audit-log' },
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
      ];
        }

    return baseItems;
    };

  const navItems = getNavItems();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              SRB Credite
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sistem de Management Credite Școlare
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {profile?.prenume} {profile?.nume}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {profile?.rol} {profile?.clasa ? `• ${profile.clasa}` : ''}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleMenu} 
              sx={{ 
                p: 0.5,
                border: '2px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }}
            >
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: 'primary.main',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {profile?.prenume?.[0]}{profile?.nume?.[0]}
              </Avatar>
            </IconButton>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 200 }
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {profile?.nume} {profile?.prenume}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }} sx={{ gap: 1.5 }}>
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">Profilul meu</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ gap: 1.5, color: 'error.main' }}>
              <LogoutIcon fontSize="small" />
              <Typography variant="body2">Deconectare</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        {/* Logo Section */}
        <Box 
          sx={{ 
            p: 3, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
          }}
        >
          <Box
            component="img"
            src="/SiglaLiceuRem.png"
            alt="Sigla Liceului"
            sx={{
              width: 96,
              height: 96,
              objectFit: 'contain',
            }}
          />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              SRB
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Credite Școlare
            </Typography>
          </Box>
        </Box>
        
        <Divider />
        
        {/* Navigation */}
        <Box sx={{ px: 2, py: 2 }}>
          <Typography 
            variant="overline" 
            color="text.secondary" 
            sx={{ px: 1.5, fontSize: '0.65rem', letterSpacing: 1 }}
          >
            Navigare
          </Typography>
          <List sx={{ mt: 1 }}>
            {navItems.map((item, index) => {
              const isSelected = location.pathname === item.path;
              return (
                <ListItemButton 
                  key={item.path}
                  selected={isSelected}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease-in-out',
                    '&.Mui-selected': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 40,
                      color: isSelected ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isSelected ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Școala Romano-Britanică
            </Typography>
            <Typography variant="caption" color="primary.main" fontWeight={600} display="block">
              An școlar {getCurrentSchoolYear()}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1, fontSize: '0.6rem' }}>
              Developed by{' '}
              <Box
                component="a"
                href="https://github.com/Dorogea"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'text.disabled', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
              >
                Eduard Dorogea
              </Box>
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerWidth}px)`,
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Box 
          sx={{ 
            p: { xs: 1.5, sm: 3 },
            animation: 'fadeInUp 0.4s ease-out',
          }}
          className="animate-fade-in-up"
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}